import React, { useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  Modal,
  Radio,
  RadioGroup,
  Typography
} from '@mui/material';
import { classes } from './styles';
import { StyledSnackbarProps } from '../../CustomUIElements/StyledSnackbar';
import RadioLabel from './RadioLabel';
import HelpTeamGraphic from './HelpTeamGraphic';
import RecruitApiClient from '../../../../RecruitApiClient';

type Props = {
  close: () => void;
  setSnackbar: StyledSnackbarProps['setSnackbarState'];
  jobId?: number;
  selectedApplicationIds?: number[];
};

const options = [
  {
    label: 'Shortlist and interview candidates',
    body: (
      <Typography>
        I need help <strong>shortlisting and interviewing candidates</strong> — Let our team handle
        the screening and interviews, so you meet only the top talent.
      </Typography>
    )
  },
  {
    label: 'Attract more candidates',
    body: (
      <Typography>
        I need help <strong>advertising the role to attract more candidates</strong> — We’ll craft a
        compelling job ad and promote it across leading job boards and targeted channels to attract
        the right candidates fast.
      </Typography>
    )
  },
  {
    label: 'End-to-end recruitment campaign',
    body: (
      <Typography>
        I need help with the entire <strong>recruitment campaign</strong> — We’ll manage the process
        from start to finish and deliver your ideal candidate.
      </Typography>
    )
  }
];

const GetRecruitmentHelpModal = ({ close, jobId, selectedApplicationIds, setSnackbar }: Props) => {
  const [selectedValue, setSelectedValue] = useState(0);

  const handleRequest = async () => {
    try {
      await RecruitApiClient.post("/api/v4/app_services/recruitment_help", {
        choice: selectedValue,
        job_id: jobId,
        application_ids: selectedApplicationIds
      })
      setSnackbar({
        state: "success",
        message: "Thanks for your request! A member of our team will reach out shortly."
      })
      close();
    } catch (error: any) {
      setSnackbar({
        state: "error",
        message: "Something went wrong. Please try again later"
      }) 
    }
  };

  return (
    <Modal open>
      <Box sx={classes.modalContent}>
        <Box sx={classes.container}>
          <Typography color="#084D6D" variant="h5" fontWeight={700} marginBottom="24px">
            Get recruitment help
          </Typography>

          <Box position="relative">
            <Box position="absolute" right={0} top="-120px">
              <HelpTeamGraphic />
            </Box>
          </Box>

          <FormControl>
            <FormLabel sx={{ marginBottom: '16px' }}>
              <Typography color="#5D5D5D" variant="subtitle2">
                Select one of the options below:
              </Typography>
            </FormLabel>
            <RadioGroup
              sx={{ gap: '16px' }}
              value={selectedValue}
              onChange={(e) => setSelectedValue(Number(e.target.value))}
            >
              {options.map((option, idx) => (
                <FormControlLabel
                  key={option.label}
                  value={idx}
                  label={<RadioLabel title={option.label}>{option.body}</RadioLabel>}
                  control={
                    <Radio
                      sx={{
                        color: '#D0D0D0',
                        '&.Mui-checked': { color: '#5BC4BF' }
                      }}
                    />
                  }
                />
              ))}
            </RadioGroup>
          </FormControl>

          <Box sx={classes.modalActions}>
            <Button disableElevation variant="text" sx={classes.buttonRedLight} onClick={close}>
              Cancel
            </Button>
            <Button sx={classes.requestButton} onClick={handleRequest}>
              Request
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default GetRecruitmentHelpModal;
